import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useToast } from '@/hooks/useToast'

/* blocks */
import Link from '@/atoms/Link/Link'
import Accordion from '@/atoms/Accordion/Accordion'

/* assets */
import { ReactComponent as LinkIcon } from 'public/assets/icons/events/link.svg'
import { ReactComponent as TwitterIcon } from 'public/assets/icons/events/twitter.svg'
import { ReactComponent as FacebookIcon } from 'public/assets/icons/events/facebook.svg'
import { ReactComponent as PlusIcon } from 'public/assets/icons/plus.svg'

/* styles */
import styles from '../../_.module.css'

interface Props {
  class?: string
  content: StrapiSharedContentComponent[]
  testimonial: {
    id: string
    authorName: string
    authorDesignation: string
    authorCompany: string
    review: string
    authorImage: {
      data: {
        attributes: StrapiImageFile
      }
    }
    authorCompanyImage: {
      data: {
        attributes: StrapiImageFile
      }
    }
  }
  videoUrl?: string
}

function CaseStudyContent({ content, ...props }: Props) {
  const [activeSection, setActiveSection] = useState<string | null>('')
  const [activeKey, setActiveKey] = useState<number | string | undefined>(
    content[0]?.id
  )
  // eslint-disable-next-line @typescript-eslint/no-array-constructor
  const containerRefs = useRef(new Array())
  const { toast, showToast } = useToast()
  const router = useRouter()

  const links = 'https://www.peppercontent.io' + router.asPath
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(links)
      showToast({
        title: 'Link copied to clipboard',
        type: 'success',
        swipeDirection: 'up'
      })
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    const currentElement = containerRefs.current
    const options = {
      root: null,
      rootMargin: '-30px 0px 0px 0px'
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id)
        }
      })
    }, options)

    currentElement.forEach(section => {
      observer.observe(section)
    })

    return () => {
      currentElement.forEach(sectionRef => {
        if (sectionRef) {
          observer.unobserve(sectionRef)
        }
      })
    }
  }, [])

  return (
    <section className="container | py-9 md:py-18">
      <div
        className={clsx(
          props.class,
          'font-heading grid grid-cols-1 md:grid-cols-12 md:gap-8 | mb-9 md:mb-18'
        )}
      >
        <div className="hidden md:block md:col-span-4 sticky h-fit w-full top-22">
          <div className="border border-gray-20 rounded-xl | md:mb-7 md:py-7">
            <h2 className="text-lg md:text-xl text-blue-web-90 font-semibold | md:px-6 mb-6">
              In this story
            </h2>
            {content.map(contentHeader => (
              <Link
                key={contentHeader.id}
                className={clsx(
                  'inline-block hover:border-l-2 hover:text-blue-50 hover:font-semibold | w-full md:px-6 mb-4',
                  `${
                    activeSection === `section-${contentHeader.id}`
                      ? 'text-blue-50 font-semibold border-l-2'
                      : ''
                  }`
                )}
                href={`#section-${contentHeader.id}`}
                onClick={() => setActiveSection(`section-${contentHeader.id}`)}
              >
                {contentHeader.title}
              </Link>
            ))}
            <hr className="text-gray-20 | md:mx-6 mb-6" />
            <div className="bg-blue-web-50 rounded-md | text-center py-2 px-4 md:mx-6">
              <Link
                href="/book-a-demo/"
                className="font-body text-lg text-white font-medium"
              >
                Book a Demo
              </Link>
            </div>
          </div>

          <div className="flex justify-start md:justify-end items-center | mb-12 md:mb-0">
            <span className="font-body text-base font-medium text-gray-70 flex-shrink-0 mx-3">
              Share this case study
            </span>
            <hr className="w-full text-gray-40 hidden md:block" />
            <div className="flex items-center gap-4 md:gap-5 | text-black">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?quote=${SHARE_TEXT}&u=${URL}`}
                target="_blank"
                rel="noreferrer noopener"
                title="Facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href={`https://twitter.com/share?text=${SHARE_TEXT}`}
                target="_blank"
                rel="noreferrer noopener"
                title="Twitter"
              >
                <TwitterIcon
                  width="25"
                  height="25"
                  className="text-blue-web-90"
                />
              </a>
              <button onClick={copyLink} className="shareicon">
                <LinkIcon />
              </button>
              {toast}
            </div>
          </div>
        </div>
        <div className="block md:hidden | mb-10">
          <Accordion
            activeKey={activeKey}
            title="In this story"
            handleChange={(key, isOpen) => {
              isOpen ? setActiveKey(key) : setActiveKey('')
            }}
            className={clsx(
              styles.accordion,
              'font-heading text-xl lg:text-2xl font-semibold | md:w-full'
            )}
            Icon={PlusIcon}
          >
            {content.map(contentHeader => (
              <Link
                key={contentHeader.id}
                className={clsx(
                  'inline-block text-sm font-normal | w-full px-6 mb-4',
                  `${
                    activeSection === `section-${contentHeader.id}`
                      ? 'text-blue-50 font-semibold border-l-2'
                      : ''
                  }`
                )}
                href={`#section-${contentHeader.id}`}
                onClick={() => setActiveSection(`section-${contentHeader.id}`)}
              >
                {contentHeader.title}
              </Link>
            ))}
            <hr className="text-gray-20 | mb-6" />
            <div className="bg-blue-web-50 rounded-md | text-center py-2 px-4 md:mx-6">
              <Link
                href="/book-a-demo/"
                className="font-body text-white font-medium"
              >
                Book a Demo
              </Link>
            </div>
          </Accordion>
          <hr className="text-gray-20" />
        </div>
        <div className="md:col-span-8 | lg:ml-10">
          {props.testimonial ? (
            <section id="#testimonial">
              <div
                className={clsx(
                  styles.testimonialReview,
                  'text-lg md:text-2xl text-blue-web-90 font-semibold'
                )}
                dangerouslySetInnerHTML={{ __html: props.testimonial.review }}
              />
              <div className="flex items-center gap-3 | mb-10">
                {props.testimonial.authorImage?.data?.attributes.url ? (
                  <Image
                    src={props.testimonial.authorImage?.data?.attributes.url}
                    alt={
                      props.testimonial.authorImage?.data?.attributes
                        .alternativeText ?? ''
                    }
                    width={
                      props.testimonial.authorImage?.data?.attributes.width
                    }
                    height={
                      props.testimonial.authorImage?.data?.attributes.height
                    }
                    className="w-18 h-18 rounded-full"
                  />
                ) : null}
                <div className="">
                  <div className="font-body text-lg font-medium">
                    {props.testimonial.authorName}
                  </div>
                  <div>
                    {`${props.testimonial.authorDesignation}, ${props.testimonial.authorCompany}`}
                  </div>
                </div>
              </div>
              <hr className="text-gray-20 | mb-10" />
            </section>
          ) : null}
          {props.videoUrl ? (
            <iframe
              title="Case Study Video Iframe"
              src={props.videoUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={clsx(styles.iframe, 'aspect-video w-full rounded-xl')}
            />
          ) : null}
          {content.map(item => (
            <section
              id={`section-${item.id}`}
              ref={(e: HTMLElement) => containerRefs.current.push(e)}
              key={item.id}
              className="title scroll-m-18 md:py-5"
            >
              <div className="link text-2xl md:text-3xl font-semibold | mb-5 md:mb-7">
                {item.title}
              </div>
              <div
                className="text-sm md:text-base"
                dangerouslySetInnerHTML={{
                  __html: item.description
                }}
              />
            </section>
          ))}
        </div>
      </div>
      <hr className="text-gray-20" />
    </section>
  )
}

const URL = 'https://www.peppercontent.io/'
const SHARE_TEXT =
  encodeURI(`Discover this customer's journey to organic marketing success!
${URL} @pepper_content`)

export default CaseStudyContent
